<template>
    <v-container
            id="detailed_orders"
            fluid
            tag="section"
            class="pl-2 pt-1 pr-2"
            style="overflow:auto"
    >
        <DetailedOrdersFilters @onFiltersChanged="onFiltersChanged"/>
        <v-data-table
                :options.sync="options"
                :loading="loading"
                :loading-text="$t('global.loading')"
                :items="detailedOrderItems"
                :server-items-length="totalItems"
                :headers="headers"
                :footer-props="footerProps"
                :no-data-text="$t('global.no_data')"
                fixed-header
        >
            <template v-slot:item.woo_commerce_order_link="{ item }">
                    <v-btn fab large
                           link
                           target="_blank"
                           :color="getOrderItemColor(item)"
                           :href="getImparfaiteOrderLink(item.order.woo_commerce_order_id)"
                    >
                        <v-icon small color="indigo lighten-2">mdi-open-in-new</v-icon>
                    </v-btn>
            </template>
            <template v-slot:item.woo_commerce_order_id="{ item }">
                <div class="text-h6 pb-5">
                    {{item.order.woo_commerce_order_id}}
                </div>
                {{item.order.created_datetime | momentDate }}
                <div class="pt-5" v-if="item.shipments.toCustomer">
                    {{item.shipments.toCustomer.recipient_address_recipient_name}}
                </div>
            </template>
            <template v-slot:item.product="{ item }">
                <v-card class="mx-auto mb-2"
                        max-width="350"
                        flat
                >
                    <v-list-item two-line>
                        <v-list-item-avatar
                                tile
                                size="80"
                                color="grey">
                            <v-img :src="item.product.images[0]"></v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title class="text-h6 mb-1"
                                               :title="item.product.title">
                                {{ item.product.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                                {{ item.product.woo_commerce_product_sku }}
                                <v-btn small icon color="indigo lighten-2"
                                       link
                                       target="_blank"
                                       :href=getImparfaiteLinkToProduct(item.product.woo_commerce_product_id)
                                >
                                    <v-icon small >mdi-open-in-new</v-icon>
                                </v-btn>
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                    <v-card-actions>
                        <div class="text-body-1 pl-2">
                            {{item.store.name}}
                        </div>
                        <v-spacer></v-spacer>
                        <v-tooltip left
                                   v-if="item.product.in_imparfaite_warehouse">
                            <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs"
                                            v-on="on"
                                            left color="red accent-3">mdi-warehouse</v-icon>
                            </template>
                            {{ $t('products.in_imparfaite_warehouse') }}
                        </v-tooltip>
                    </v-card-actions>
                </v-card>
            </template>

            <template v-slot:item.status="{ item }">
                <v-list-item three-line>
                    <v-list-item-content>
                        <v-list-item-title>
                            <v-chip center
                                    :class="getStatusColor(item.order.status)">
                                {{ getOrderStatusLabel(item.order.status) }}
                            </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle center>
                            {{ getEstimatedPaymentDate(item) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle center>
                            {{item.order.shipping_class }}
                            <br/>
                            <v-chip center v-if="item.order.warehouse_status"
                                    :class="getStatusColor(item.order.warehouse_status)">
                                {{ getOrderStatusLabel(item.order.warehouse_status) }}
                            </v-chip>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </template>

            <template v-slot:item.order_shipping_to_imparfaite_status="{ item }">
                <ShipmentListDetail  v-if="item.shipments.toImparfaite"
                                     v-bind:shipment="item.shipments.toImparfaite"  @onShipmentUpdated="onShipmentUpdated">
                </ShipmentListDetail>
            </template>
            <template v-slot:item.order_shipping_to_customer_status="{ item }">
                <ShipmentListDetail  v-if="item.shipments.toCustomer"
                                     v-bind:shipment="item.shipments.toCustomer"  @onShipmentUpdated="onShipmentUpdated">
                </ShipmentListDetail>
            </template>

            <template v-slot:item.order_ready_to_be_prepared_for_customer="{ item }">
                <v-switch
                        @change="certifyConform(item)"
                        v-model="item.order.ready_to_be_prepared_for_customer"
                >
                </v-switch>
            </template>

            <template v-slot:item.order_administrator_notes="{ item }">
                {{item.order.administrator_notes }}
            </template>

            <template v-slot:item.order_shipping_return="{ item }">
                {{item.order.return_shipment_id }}
            </template>


            <template v-slot:item.menu="{ item }">
                <v-menu
                        bottom left>
                    <template v-slot:activator="{ on }">
                        <v-btn
                                icon
                                v-on="on"
                        >
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item @click="updateNotes(item)">
                            <v-list-item-title>
                                <v-icon left>mdi-information</v-icon>
                                {{ $t('contextual_menu.detailed_orders.update_notes') }}
                            </v-list-item-title>
                        </v-list-item>


                        <v-list-item @click="transfer(item)">
                            <v-list-item-title>
                                <v-icon left>mdi-wallet-plus-outline</v-icon>
                                {{ $t('contextual_menu.detailed_orders.transfer') }}
                            </v-list-item-title>
                        </v-list-item>

                        <v-list-item
                                v-if="item.order.status === 'PENDING_CUSTOMER_ACCEPTANCE' || item.order.status === 'PENDING_DELIVERY'"
                                @click="returnOrder(item)"
                        >
                            <v-list-item-title>
                                <v-icon left>mdi-cash-refund</v-icon>
                                {{ $t('contextual_menu.detailed_orders.return') }}
                            </v-list-item-title>
                        </v-list-item>

                        <v-list-item
                                v-if="item.order.status === 'PENDING_REFUND'"
                                @click="cancelReturnOrder(item)"
                        >
                            <v-list-item-title>
                                <v-icon left>mdi-cancel</v-icon>
                                {{ $t('contextual_menu.detailed_orders.cancel_return') }}
                            </v-list-item-title>
                        </v-list-item>

                    </v-list>
                </v-menu>
            </template>
        </v-data-table>
        <v-dialog v-model="dialog.visible" max-width="500px">
            <v-card>
                <v-card-title>
                    {{ dialogTitle }}
                </v-card-title>

                <v-card-text>
                    <div class="text-subtitle-1 mb-4 pb-4 pt-4">{{ dialogText}}</div>
                    <div class="text-caption mb-2"> {{ dialogSubtext}}</div>
                </v-card-text>

                <v-card-actions>
                    <v-btn outlined color="red darken-2" @click="close">
                        <v-icon right>mdi-cancel</v-icon>Annuler
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn raised color="green darken-1" @click="execute">
                        <v-icon left>mdi-check-outline</v-icon>Oui
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="returnDialog.visible" max-width="500px">
            <v-card>
                <v-card-title>
                    {{ returnDialogTitle }}
                </v-card-title>

                <v-card-text>
                    <div class="text-subtitle-1 mb-4 pb-4 pt-4">{{ returnDialogText}}</div>
                    <div class="text-caption mb-2"> {{ returnDialogSubtext}}</div>
                </v-card-text>

                <v-card-actions>
                    <v-btn outlined color="red darken-2" @click="close">
                        <v-icon right>mdi-cancel</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn raised color="#f9dbc2" @click="executeReturn('warehouse')">
                        <v-icon left>mdi-check-outline</v-icon>A l'atelier
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn raised color="green lighten-1" @click="executeReturn('store')">
                        <v-icon left>mdi-check-outline</v-icon>Vendeur
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="adminDialog.visible" max-width="500px">
            <v-card>
                <v-card-title>
                    {{ adminDialogTitle }}
                </v-card-title>

                <v-card-text>
                    <div class="text-subtitle-1 mb-4 pb-4 pt-4">{{ adminDialogText}}</div>
                    <v-row>
                        <v-textarea
                                v-model="adminDialog.notes"
                            background-color="amber lighten-4"
                            color="orange orange-darken-4"
                            label="Note"></v-textarea>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-btn outlined color="red darken-2" @click="closeAdminDialog">
                        <v-icon right>mdi-cancel</v-icon>Annuler
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn raised color="green darken-1" @click="saveAdminDialog">
                        <v-icon left>mdi-check-outline</v-icon>Oui
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
                v-model="snackbar.visible"
                multi-line
                :color="snackbar.color"
                :timeout="snackbar.timeout"
                right
                shaped
                top
        >
            {{ snackbar.text }}

            <template v-slot:action="{ attrs }">
                <v-btn
                        color="primary"
                        text
                        v-bind="attrs"
                        @click="snackbar.visible = false"
                >
                    Fermer
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
    import mappingStatusColors from '../../../config/mapping-status-colors.json'
    import {mapGetters, mapState} from "vuex";
    import {createQueryString} from "../../../helpers/createQueryString";
    import DetailedOrdersFilters from '../components/DetailedOrdersFilters'
    import moment from "moment";
    import ShipmentListDetail from "../../components/ShipmentListDetail";

    export default {
        name: 'DetailedOrders.List',
        components: {ShipmentListDetail, DetailedOrdersFilters},
        data() {
            return {

                dialog:{
                    visible: false,
                    title: '',
                    text: '',
                    subtext: '',
                    action: '',
                },
                returnDialog:{
                    visible: false,
                    title: '',
                    text: '',
                    subtext: '',
                    action: '',
                },
                adminDialog:{
                    visible: false,
                    title: '',
                    text: '',
                    notes: '',
                    conform: '',
                },
                snackbar: {
                    timeout:5,
                    visible: false,
                    text: '',
                    color: '',
                    y: 'top',
                    x : 'right',
                },
                editedIndex: -1,
                editedItem: {
                    title: '',
                    id: '',
                },
                defaultItem: {
                    id: '',
                },
                loading: false,
                store: {},
                headers: [
                    {text: '', value: 'woo_commerce_order_link', sortable: false, width:80},
                    {text: this.$i18n.t('global.wc_order_id'), value: 'woo_commerce_order_id', sortable: false, width:150},
                    {text: this.$i18n.t('global.products'), value: 'product', sortable: false},
                    {text: this.$i18n.t('global.status'), value: 'status', sortable: false, align: 'center'},
                    {text: this.$i18n.t('admin.imparfaite_shipping'), value: 'order_shipping_to_imparfaite_status', sortable: false, width:220, align: 'center'},
                    {text: this.$i18n.t('admin.client_shipping'), value: 'order_shipping_to_customer_status', sortable: false, width:220, align: 'center'},
                    {text: this.$i18n.t('admin.return'), value: 'order_shipping_return', sortable: false},
                    {text: this.$i18n.t('admin.is_ready'), value: 'order_ready_to_be_prepared_for_customer', sortable: false},
                    {text: this.$i18n.t('admin.notes'), value: 'order_administrator_notes', sortable: false},
                    {text: '', value: 'menu', sortable: false, width: '50px'},
                ],
                footerProps: {'items-per-page-options': [10], 'show-first-last-page': true},
                options: {
                    page: 1,
                    itemsPerPage: 10,
                    sortBy: [],
                    sortDesc: [],
                    groupBy: [],
                    groupDesc: [],
                    multiSort: false,
                    mustSort: false,
                },
                detailedOrderItems: [],
                totalItems: 0
            };
        },
        methods: {
            onShipmentUpdated() {
                this.load();
            },
            onFiltersChanged(filters) {
                this.filters = {
                    'order_item:wooCommerceOrderId': filters.order_item_woo_commerce_order_id,
                    'order_item:shippingClass': filters.order_item_shipping_class,
                    'order_item:status': filters.order_item_status,
                    'order_item:warehouseStatus': filters.order_item_warehouse_status,
                    'product:wooCommerceProductSku:like': filters.product_woo_commerce_product_sku,
                    'store:name:like' : filters.store_name,
                    'order_item:createdDatetime:lte' : filters.order_item_created_datetime_lte,
                    'order_item:createdDatetime:gte' : filters.order_item_created_datetime_gte,
                    'shipment:trackingNumber:like' : filters.shipment_tracking_number,
                    'shipment:recipientAddressRecipientName:like' : filters.shipment_recipient_address_recipient_name,
                };
                this.options.page = 1;
                this.load();
            },
            load() {
                this.loading = true;
                this.detailedOrderItems = [];
                let params = {page: this.options.page, ...this.$route.params, ...this.filters, order: {}};

                // Define sorting parameters.
                if (this.options.sortBy.length) {
                    params.order[this.options.sortBy[0]] = this.options.sortDesc[0] ? 'desc' : 'asc';
                }

                const queryString = createQueryString(params);
                this.$axios.get('detailed_orders/?' + queryString)
                    .then(response => {
                        this.loading = false;
                        this.detailedOrderItems = response.data['items'];
                        this.totalItems = response.data['total'];
                    });
            },
            transfer(item) {
                this.dialog.title = this.$i18n.t('dialog.detailed_orders.transfer.title');
                this.dialog.text = this.$i18n.t('dialog.detailed_orders.transfer.text', {
                    amount: this.formatPrice(item.order.paid_amount),
                    wooProductSku: item.product.woo_commerce_product_sku,
                    wooOrderId: item.order.woo_commerce_order_id,
                    storeName: item.store.name
                });
                this.dialog.subtext =  this.$i18n.t('dialog.detailed_orders.transfer.subtext');
                this.dialog.action = 'transfer';

                this.editedIndex = this.detailedOrderItems.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.dialog.visible = true
            },
            certifyConform(item) {
                this.loading = true;
                this.snackbar.visible = true;
                this.snackbar.text =this.$i18n.t('snackbar.detailed_orders.conformity.loading');
                this.snackbar.color = 'info';

                this.$axios.post('order_items/warehouse/conformities', {
                    'order_item_ids': [item.order.id],
                    'ready_to_be_prepared_for_customer' : item.order.ready_to_be_prepared_for_customer
                })
                    .then(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.conformity.done');
                        this.snackbar.color = 'green';
                        this.load();
                    })
                    .catch(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.conformity.failed');
                        this.snackbar.color = 'error';
                        this.load();
                    });
            },
            executeTransfer(item) {
                this.$axios.post('order_items/'+item.order.id+'/transfers', {force: true})
                    .then(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.transfer.done');
                        this.snackbar.color = 'green';
                        this.snackbar.timeout = 5;
                        this.load();
                    })
                    .catch((error) => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.transfer.failed')
                            + ' : ' + error.response.data['hydra:description'];
                        this.snackbar.color = 'error';
                        this.snackbar.timeout = -1;
                        this.load();
                    });
            },
            returnOrder(item) {
                this.returnDialog.title = this.$i18n.t('dialog.detailed_orders.return.title');
                this.returnDialog.text = this.$i18n.t('dialog.detailed_orders.return.text', {
                    wooProductSku: item.product.woo_commerce_product_sku,
                    wooOrderId: item.order.woo_commerce_order_id,
                    storeName: item.store.name
                });
                this.returnDialog.subtext =  this.$i18n.t('dialog.detailed_orders.return.subtext');
                this.returnDialog.action = 'return';

                this.editedIndex = this.detailedOrderItems.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.returnDialog.visible = true
            },
            executeReturn(type) {
                this.$axios.post('order_items/'+this.editedItem.order.id+'/returns/request', {
                    'return_type' : type,
                })
                    .then(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.return.done');
                        this.snackbar.color = 'green';
                        this.load();
                        this.snackbar.timeout = 5;
                        this.returnDialog.visible = false;
                    })
                    .catch(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text =this.$i18n.t('snackbar.detailed_orders.return.failed');
                        this.snackbar.color = 'error';
                        this.load();
                        this.snackbar.timeout = -1;
                        this.returnDialog.visible = false;
                    });
            },
            cancelReturnOrder(item) {
                this.dialog.title = this.$i18n.t('dialog.detailed_orders.cancel_return.title');
                this.dialog.text = this.$i18n.t('dialog.detailed_orders.cancel_return.text', {
                    wooProductSku: item.product.woo_commerce_product_sku,
                    wooOrderId: item.order.woo_commerce_order_id,
                    storeName: item.store.name
                });
                this.dialog.subtext =  this.$i18n.t('dialog.detailed_orders.cancel_return.subtext');
                this.dialog.action = 'cancel-return';

                this.editedIndex = this.detailedOrderItems.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.dialog.visible = true
            },
            executeCancelReturn(item) {
                this.$axios.post('order_items/'+item.order.id+'/returns/cancel', {})
                    .then(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.cancel_return.done');
                        this.snackbar.color = 'green';
                        this.load();
                    })
                    .catch(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text =this.$i18n.t('snackbar.detailed_orders.cancel_return.failed');
                        this.snackbar.color = 'error';
                        this.load();
                    });
            },
            close () {
                this.dialog.visible = false
                this.dialog.action = '';

                this.$nextTick(() => {
                    this.editedItem = null
                    this.editedIndex = -1
                })
            },
            execute () {
                this.loading = true;
                this.snackbar.visible = true;
                this.snackbar.text =this.$i18n.t('global.loading');
                this.snackbar.color = 'info';

                if (this.dialog.action === 'transfer') {
                    this.executeTransfer(this.editedItem);
                }
                if (this.dialog.action === 'return') {
                    this.executeReturn(this.editedItem);
                }
                if (this.dialog.action === 'cancel-return') {
                    this.executeCancelReturn(this.editedItem);
                }

                this.close()
            },
            updateNotes(item) {
                this.adminDialog.title = this.$i18n.t('dialog.detailed_orders.update_notes.title');
                this.adminDialog.text = this.$i18n.t('dialog.detailed_orders.update_notes.text', {
                    wooProductSku: item.product.woo_commerce_product_sku,
                    wooOrderId: item.order.woo_commerce_order_id,
                    storeName: item.store.name
                });

                this.adminDialog.notes = item.order.administrator_notes;
                this.adminDialog.conform = item.order.ready_to_be_prepared_for_customer;

                this.editedIndex = this.detailedOrderItems.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.adminDialog.visible = true
            },
            closeAdminDialog () {
                this.adminDialog.visible = false
                this.adminDialog.text = '';

                this.$nextTick(() => {
                    this.editedItem = null
                    this.editedIndex = -1
                })
            },
            saveAdminDialog () {
                this.loading = true;
                this.snackbar.visible = true;
                this.snackbar.text =this.$i18n.t('global.loading');
                this.snackbar.color = 'info';

                this.$axios.post('order_items/'+this.editedItem.order.id+'/notes', {
                    'administrator_notes' : this.adminDialog.notes
                })
                    .then(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text = this.$i18n.t('snackbar.detailed_orders.update_notes.done');
                        this.snackbar.color = 'green';
                        this.load();
                        this.closeAdminDialog()

                    })
                    .catch(() => {
                        this.loading = false;
                        this.snackbar.visible = true;
                        this.snackbar.text =this.$i18n.t('snackbar.detailed_orders.update_notes.failed');
                        this.snackbar.color = 'error';
                        this.load();
                        this.closeAdminDialog()
                    });
            },
            getOrderStatusLabel(status) {
                return this.$i18n.t('orders.status.' + status.toLowerCase())
            },
            getShipmentStatusLabel(status) {
                return this.$i18n.t('shipments.status_labels.' + status.toLowerCase())
            },
            getStatusColor(status) {
                return mappingStatusColors[status];
            },
            getImparfaiteLinkToProduct(wooCommerceProductId) {
                return 'https://www.imparfaiteparis.com/?post_type=product&p=' + wooCommerceProductId;
            },
            getImparfaiteOrderLink(orderId) {
                return 'https://www.imparfaiteparis.com/wp-admin/post.php?post=' + orderId +'&action=edit';
            },
            getEstimatedPaymentDate(item) {
                if (item.order.delivered_datetime) {
                    return moment(String(item.order.delivered_datetime)).add('15', 'd').format('DD/MM/YYYY')
                }
            },
            getOrderItemColor(item) {
                if (item.order.status === 'CANCELLED') {
                    return 'black';
                }

                if (item.order.status === 'PENDING_REFUND' || item.order.status === 'REFUNDED') {
                    return 'red';
                }

                if (item.shipments.toCustomer && item.shipments.toCustomer.status === 'PENDING_DEPOSIT') {
                    if (moment().diff(item.order.created_datetime) > 10) {
                        return 'orange darken-2';
                    }

                    if (moment().diff(item.order.created_datetime) > 5) {
                        return 'orange lighten-2';
                    }
                }

                return '';
            },
            formatDate(date) {
                if (date) {
                    return moment(String(date)).format('DD/MM/YYYY')
                }
                return '-';
            },
            formatPrice(value) {
                if (typeof value !== "number") {
                    return value;
                }
                var formatter = new Intl.NumberFormat('FR', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 2
                });
                return formatter.format(value);
            }
        },
        watch: {
            options: {
                handler() {
                    this.load();
                },
                deep: true
            },
            queryString: {
                handler() {
                    this.load();
                }
            }
        },
        computed: {
            ...mapGetters(['isAdmin', 'isSeller']),
            ...mapState(['userInformation']),
            queryString: function () {
                return createQueryString({page: this.page, ...this.$route.params, ...this.$route.query});
            },
            dialogTitle () {
                return this.dialog.title;
            },
            dialogText () {
                return this.dialog.text;
            },
            dialogSubtext () {
                return this.dialog.subtext;
            },
            adminDialogTitle () {
                return this.adminDialog.title;
            },
            adminDialogText () {
                return this.adminDialog.text;
            },
            returnDialogTitle () {
                return this.returnDialog.title;
            },
            returnDialogText () {
                return this.returnDialog.text;
            },
            returnDialogSubtext () {
                return this.returnDialog.subtext;
            },
        },
        mounted() {

        }
    }
</script>
